/**
 * AchievementManager - Handles achievement unlocking and persistence
 */

import { ACHIEVEMENTS, TOTAL_ACHIEVEMENTS, getAllAchievements } from '../data/achievements';
import type { Achievement, AchievementStats } from '../data/achievements';

const STORAGE_KEY = 'daynight_achievements';

export class AchievementManager {
    private unlockedAchievements: Set<string>;
    private newlyUnlocked: Achievement[];

    constructor() {
        this.unlockedAchievements = new Set();
        this.newlyUnlocked = []; // Achievements unlocked in current game session

        this.loadAchievements();
    }

    loadAchievements(): void {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const data = JSON.parse(saved);
                if (Array.isArray(data)) {
                    this.unlockedAchievements = new Set(data as string[]);
                }
            }
        } catch (e) {
            console.warn('Could not load achievements:', e);
        }
    }

    saveAchievements(): void {
        try {
            const data = Array.from(this.unlockedAchievements);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch (e) {
            console.warn('Could not save achievements:', e);
        }
    }

    /**
     * Check all achievements against current stats
     * Call this on game over
     */
    checkAchievements(stats: AchievementStats): Achievement[] {
        // Clear newly unlocked list for this session
        this.newlyUnlocked = [];

        for (const achievement of getAllAchievements()) {
            // Skip already unlocked
            if (this.unlockedAchievements.has(achievement.id)) {
                continue;
            }

            // Check if condition is met
            try {
                if (achievement.check(stats)) {
                    this.unlockAchievement(achievement.id);
                }
            } catch (e) {
                console.warn(`Error checking achievement ${achievement.id}:`, e);
            }
        }

        // Save if any new achievements were unlocked
        if (this.newlyUnlocked.length > 0) {
            this.saveAchievements();
        }

        return this.newlyUnlocked;
    }

    /**
     * Unlock a specific achievement
     */
    unlockAchievement(id: string): void {
        if (!this.unlockedAchievements.has(id)) {
            this.unlockedAchievements.add(id);
            this.newlyUnlocked.push(ACHIEVEMENTS[id]);
        }
    }

    /**
     * Check if an achievement is unlocked
     */
    isUnlocked(id: string): boolean {
        return this.unlockedAchievements.has(id);
    }

    /**
     * Get list of achievements unlocked in the current game session
     */
    getNewlyUnlocked(): Achievement[] {
        return this.newlyUnlocked;
    }

    /**
     * Clear the newly unlocked list (call when leaving game over screen)
     */
    clearNewlyUnlocked(): void {
        this.newlyUnlocked = [];
    }

    /**
     * Get all unlocked achievement objects
     */
    getUnlockedAchievements(): Achievement[] {
        return getAllAchievements().filter(a => this.unlockedAchievements.has(a.id));
    }

    /**
     * Get all locked achievement objects
     */
    getLockedAchievements(): Achievement[] {
        return getAllAchievements().filter(a => !this.unlockedAchievements.has(a.id));
    }

    /**
     * Get progress string (e.g., "15/50")
     */
    getProgress(): string {
        return `${this.unlockedAchievements.size}/${TOTAL_ACHIEVEMENTS}`;
    }

    /**
     * Get unlocked count
     */
    getUnlockedCount(): number {
        return this.unlockedAchievements.size;
    }

    /**
     * Get total achievement count
     */
    getTotalCount(): number {
        return TOTAL_ACHIEVEMENTS;
    }
}
